/**
 * Netzwerkbereiche: Router, Switches, Leitungen, BGP, Peering und IPAM.
 *
 * Diese Ansichten sind der technische Kern des Spiels. Sie zeigen bewusst echte
 * Betriebsgrößen – Sessions, AS-Pfade, Präfixzahlen, 95.-Perzentil – statt
 * abstrakter Spielwerte, damit Wissen aus der Praxis anwendbar bleibt.
 */
import { useEffect, useMemo, useState } from 'react';
import type {
  BgpNeighbor,
  BgpSession,
  IpPrefix,
  Ixp,
  Link as NetLink,
  NetworkSwitch,
  PeerRelation,
  RibEntry,
  Router as NetRouter,
} from '@hosting-sim/core';
import {
  FULL_TABLE_SIZE,
  HYGIENE_COSTS,
  HYGIENE_DESCRIPTION,
  HYGIENE_LABEL,
  assignedAddressCount,
  billable95th,
  canSellPrefix,
  cidrOf,
  describeVia,
  ixpPortLoad,
  peeringOverview,
  relationLabel,
  type HygieneMeasure,
} from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  Tabs,
  type Tone,
} from '../components/ui';
import { SellDeviceButton } from '../components/SellDevice';
import { useGame, useMetrics } from '../store/game';
import { api } from '../lib/api';
import { bytes, dec, duration, mbps, money, num, pct100 } from '../lib/format';
import { BuyHardwareDialog, DeviceStatus, HealthCell, useCatalog } from './Infrastructure';

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export function Routers() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const catalog = useCatalog();
  const [buying, setBuying] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  if (!world) return null;
  const routers = Object.values(world.routers);

  const columns: Column<NetRouter>[] = [
    {
      key: 'name',
      header: 'Router',
      sortValue: (r) => r.name,
      render: (r) => (
        <div>
          <p className="font-medium">{r.name}</p>
          <Mono className="text-panel-500">
            AS{r.bgp.localAsn} · {r.bgp.routerId}
          </Mono>
        </div>
      ),
    },
    { key: 'state', header: 'Zustand', render: (r) => <DeviceStatus device={r} /> },
    { key: 'site', header: 'Standort', hideOnMobile: true, sortValue: (r) => world.datacenters[r.datacenterId]?.city ?? '', render: (r) => world.datacenters[r.datacenterId]?.city },
    {
      key: 'ports',
      header: 'Ports',
      align: 'right',
      hideOnMobile: true,
      sortValue: (r) => r.ports.filter((p) => p.linkId).length,
      render: (r) => (
        <span className="tabular text-xs">
          {r.ports.filter((p) => p.linkId).length} / {r.ports.length}
        </span>
      ),
    },
    {
      key: 'sessions',
      header: 'BGP-Sessions',
      align: 'right',
      sortValue: (r) => r.bgp.neighbors.length,
      render: (r) => {
        const enabled = r.bgp.neighbors.filter((n) => n.enabled).length;
        const over = enabled > r.maxBgpSessions;
        return (
          <span className={`tabular text-xs ${over ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>
            {enabled} / {r.maxBgpSessions}
          </span>
        );
      },
    },
    {
      key: 'fib',
      header: 'FIB',
      align: 'right',
      hideOnMobile: true,
      sortValue: (r) => r.fibCapacity,
      render: (r) => <span className="tabular text-xs">{num(r.fibCapacity)}</span>,
    },
    {
      key: 'cpu',
      header: 'CPU',
      align: 'right',
      sortValue: (r) => r.cpuLoad,
      render: (r) => <span className="tabular text-xs">{Math.round(r.cpuLoad * 100)} %</span>,
    },
    { key: 'health', header: 'Zustand', align: 'right', render: (r) => <HealthCell health={r.health} temperatureC={r.temperatureC} /> },
  ];

  return (
    <>
      <PageHeader
        title="Router"
        description="Kernkomponenten des Netzes: Ports, Weiterleitungskapazität und Control Plane."
        actions={
          <Button variant="primary" onClick={() => setBuying(true)}>
            Router kaufen
          </Button>
        }
      />

      <Card padded={false}>
        <DataTable columns={columns} rows={routers} rowKey={(r) => r.id} onRowClick={(r) => setSelected(r.id)} empty="Kein Router vorhanden." />
      </Card>

      <BuyHardwareDialog
        open={buying}
        onClose={() => setBuying(false)}
        kind="router"
        catalog={catalog}
        onBuy={async (rackId, modelKey) => (await run({ type: 'hw/buyRouter', rackId: rackId as never, modelKey })).ok}
      />

      {selected && <RouterDetail routerId={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function RouterDetail({ routerId, onClose }: { routerId: string; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [moving, setMoving] = useState<string | null>(null);
  const router = world?.routers[routerId as never];
  if (!world || !router) return null;

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title={`${router.name} – Ports und Konfiguration`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button onClick={() => void run({ type: 'hw/reboot', kind: 'router', id: router.id })}>Neu starten</Button>
          <SellDeviceButton kind="router" id={router.id} name={router.name} size="md" onSold={onClose} />
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Modell">{router.model}</Field>
          <Field label="Weiterleitung">{mbps(router.forwardingCapacityMbps)}</Field>
          <Field label="FIB-Kapazität">{num(router.fibCapacity)} Einträge</Field>
          <Field label="Max. Sessions">{router.maxBgpSessions}</Field>
        </div>

        {router.fibCapacity < FULL_TABLE_SIZE && (
          <Notice tone="warning" title="Kleine Weiterleitungstabelle">
            Dieser Router kann keine vollständige Internet-Routingtabelle halten ({num(FULL_TABLE_SIZE)} Präfixe). Bei einem Upstream muss
            deshalb „nur Default-Route“ aktiviert bleiben.
          </Notice>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="label">Ports</p>
            <p className="text-2xs text-panel-500 dark:text-panel-400">
              {router.ports.filter((p) => p.linkId).length} belegt, {router.ports.filter((p) => !p.linkId).length} frei
            </p>
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {router.ports.map((port) => (
              <RouterPortRow key={port.index} router={router} port={port} onMove={(id: string) => setMoving(id)} />
            ))}
          </div>
        </div>

        <div>
          <p className="label mb-2">Statische Routen</p>
          {router.staticRoutes.length === 0 ? (
            <p className="text-xs text-panel-500">Keine statischen Routen konfiguriert.</p>
          ) : (
            <ul className="space-y-1">
              {router.staticRoutes.map((route) => (
                <li key={route.prefix} className="flex items-center justify-between gap-2 text-xs">
                  <Mono>
                    {route.prefix} via {route.nextHop} (AD {route.distance})
                  </Mono>
                  <Button size="sm" variant="ghost" onClick={() => void run({ type: 'router/removeStaticRoute', routerId: router.id, prefix: route.prefix })}>
                    entfernen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="label mb-2">Zugriffslisten</p>
          {router.acls.length === 0 ? (
            <p className="text-xs text-panel-500">Keine ACL konfiguriert.</p>
          ) : (
            router.acls.map((acl) => (
              <div key={acl.name} className="surface-muted p-2">
                <p className="mb-1 text-xs font-medium">{acl.name}</p>
                <ul className="space-y-0.5">
                  {acl.entries.slice(0, 8).map((entry) => (
                    <li key={entry.seq}>
                      <Mono className={entry.action === 'deny' ? 'text-red-600 dark:text-red-400' : ''}>
                        {entry.seq} {entry.action} {entry.protocol} {entry.source} → {entry.destination}
                      </Mono>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      {moving && <MoveLinkDialog linkId={moving} onClose={() => setMoving(null)} />}
    </Modal>
  );
}

/**
 * Eine Portzeile mit ihren Aktionen.
 *
 * Vorher war die Liste reine Anzeige – man sah, dass ein Port frei ist, konnte
 * aber nichts damit tun. Jetzt lässt sich der Port abschalten und eine Strecke
 * auf einen anderen Port oder Router legen, ohne den Vertrag zu kündigen.
 */
function RouterPortRow({
  router,
  port,
  onMove,
}: {
  router: NetRouter;
  port: NetRouter['ports'][number];
  onMove: (linkId: string) => void;
}) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  if (!world) return null;

  const link = port.linkId ? world.links[port.linkId] : null;
  const disabled = port.enabled === false;

  return (
    <div className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
      <span className="w-8 shrink-0 font-mono text-panel-500">{port.index}</span>
      <StatusDot
        tone={disabled ? 'danger' : link ? (link.state === 'up' ? 'success' : link.state === 'degraded' ? 'warning' : 'danger') : 'muted'}
      />
      <span className="min-w-0 flex-1 truncate">
        {link ? link.name : <span className="text-panel-400">frei</span>}
        {disabled && <Badge tone="danger">aus</Badge>}
      </span>
      <span className="tabular shrink-0 text-panel-500">{mbps(port.speedMbps)}</span>

      <Button
        size="sm"
        variant="ghost"
        title={disabled ? 'Port wieder aktivieren' : 'Port administrativ abschalten – die Sitzung darüber fällt'}
        onClick={() => void run({ type: 'router/setPort', routerId: router.id, portIndex: port.index, enabled: disabled })}
      >
        {disabled ? 'an' : 'aus'}
      </Button>
      {link && (
        <Button size="sm" variant="ghost" title="Auf einen anderen Port oder Router legen" onClick={() => onMove(link.id)}>
          umstecken
        </Button>
      )}
    </div>
  );
}

/**
 * Strecke auf einen anderen Port legen.
 *
 * Der praktische Fall: Der Transit hängt am kleinen Einstiegsrouter, ein
 * größerer ist eingebaut. Die BGP-Sitzung zieht mit um; die Strecke ist dabei
 * kurz unterbrochen, weil tatsächlich ein Kabel gezogen wird.
 */
function MoveLinkDialog({ linkId, onClose }: { linkId: string; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const link = world?.links[linkId as never];

  const options = useMemo(() => {
    if (!world || !link) return [];
    const list: { value: string; label: string }[] = [];
    for (const r of Object.values(world.routers)) {
      for (const p of r.ports) {
        if (p.linkId) continue;
        if (p.speedMbps < link.capacityMbps) continue;
        list.push({ value: `${r.id}:${p.index}`, label: `${r.name} · Port ${p.index} · ${mbps(p.speedMbps)}` });
      }
    }
    return list;
  }, [world, link]);

  useEffect(() => {
    if (options.length > 0 && !options.some((o) => o.value === target)) setTarget(options[0].value);
  }, [options, target]);

  if (!world || !link) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${link.name} umstecken`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={options.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                const [routerId, portIndex] = target.split(':');
                const result = await run({
                  type: 'link/movePort',
                  linkId: link.id,
                  targetRouterId: routerId as never,
                  targetPortIndex: Number(portIndex),
                });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Umstecken
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Strecke">{link.name}</Field>
          <Field label="Kapazität">{mbps(link.capacityMbps)}</Field>
        </div>

        {options.length === 0 ? (
          <Notice tone="warning" title="Kein passender Port frei">
            Es gibt keinen freien Router-Port, der {mbps(link.capacityMbps)} trägt. Erst einen Port freimachen oder einen
            größeren Router einsetzen.
          </Notice>
        ) : (
          <>
            <Select label="Zielport" value={target} onChange={(e) => setTarget(e.target.value)}>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Notice tone="info">
              Wechselt dabei der Router, zieht die BGP-Sitzung mit um und wird neu aufgebaut. Die Strecke ist für kurze Zeit
              unterbrochen.
            </Notice>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Switches
// ---------------------------------------------------------------------------

export function Switches() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const catalog = useCatalog();
  const [buying, setBuying] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  if (!world) return null;
  const switches = Object.values(world.switches);

  const columns: Column<NetworkSwitch>[] = [
    { key: 'name', header: 'Switch', sortValue: (s) => s.name, render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'state', header: 'Zustand', render: (s) => <DeviceStatus device={s} /> },
    { key: 'site', header: 'Standort', hideOnMobile: true, render: (s) => world.datacenters[s.datacenterId]?.city },
    {
      key: 'ports',
      header: 'Belegte Ports',
      align: 'right',
      sortValue: (s) => s.ports.filter((p) => p.connectedDevice).length,
      render: (s) => (
        <span className="tabular text-xs">
          {s.ports.filter((p) => p.connectedDevice).length} / {s.ports.length}
        </span>
      ),
    },
    { key: 'capacity', header: 'Backplane', align: 'right', hideOnMobile: true, render: (s) => <span className="tabular text-xs">{mbps(s.switchingCapacityMbps)}</span> },
    { key: 'l3', header: 'Layer 3', align: 'center', render: (s) => (s.layer3 ? <Badge tone="success">ja</Badge> : <Badge tone="muted">nein</Badge>) },
  ];

  return (
    <>
      <PageHeader
        title="Switches"
        description="Anbindung der Server, VLAN-Zuordnung und Uplinks."
        actions={
          <Button variant="primary" onClick={() => setBuying(true)}>
            Switch kaufen
          </Button>
        }
      />

      <Card padded={false}>
        <DataTable columns={columns} rows={switches} rowKey={(s) => s.id} onRowClick={(s) => setSelected(s.id)} empty="Kein Switch vorhanden." />
      </Card>

      <BuyHardwareDialog
        open={buying}
        onClose={() => setBuying(false)}
        kind="switch"
        catalog={catalog}
        onBuy={async (rackId, modelKey) => (await run({ type: 'hw/buySwitch', rackId: rackId as never, modelKey })).ok}
      />

      {selected && <SwitchDetail switchId={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function SwitchDetail({ switchId, onClose }: { switchId: string; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const sw = world?.switches[switchId as never];
  const [editing, setEditing] = useState<number | null>(null);
  const [mode, setMode] = useState<'access' | 'trunk'>('access');
  const [vlan, setVlan] = useState('100');

  if (!world || !sw) return null;

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title={`${sw.name} – Portbelegung`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button onClick={() => void run({ type: 'hw/reboot', kind: 'switch', id: sw.id })}>Neu starten</Button>
          <SellDeviceButton kind="switch" id={sw.id} name={sw.name} size="md" onSold={onClose} />
        </>
      }
    >
      <div className="space-y-1.5">
        {sw.ports.map((port) => (
          <div key={port.index} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
            <span className="w-8 shrink-0 font-mono text-panel-500">{port.index}</span>
            <Badge tone={port.mode === 'trunk' ? 'info' : 'neutral'}>{port.mode === 'trunk' ? 'Trunk' : 'Access'}</Badge>
            <span className="shrink-0">VLAN {port.vlanId}</span>
            <span className="min-w-0 flex-1 truncate">
              {port.connectedDevice ? (
                port.description || port.connectedDevice.kind
              ) : (
                <span className="text-panel-400">nicht belegt</span>
              )}
            </span>
            <span className="tabular shrink-0 text-panel-500">{mbps(port.speedMbps)}</span>
            {port.enabled === false && <Badge tone="danger">abgeschaltet</Badge>}
            <Button
              size="sm"
              variant="ghost"
              title={port.enabled === false ? 'Port wieder aktivieren' : 'Port administrativ abschalten'}
              onClick={() =>
                void run({ type: 'switch/setPortEnabled', switchId: sw.id, portIndex: port.index, enabled: port.enabled === false })
              }
            >
              {port.enabled === false ? 'aktivieren' : 'abschalten'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(port.index);
                setMode(port.mode);
                setVlan(String(port.vlanId));
              }}
            >
              ändern
            </Button>
          </div>
        ))}
      </div>

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={`Port ${editing} konfigurieren`}>
        <div className="space-y-3">
          <Select label="Modus" value={mode} onChange={(e) => setMode(e.target.value as 'access' | 'trunk')}>
            <option value="access">Access – ein VLAN, für Endgeräte</option>
            <option value="trunk">Trunk – mehrere VLANs, für Uplinks</option>
          </Select>
          <Input label="VLAN-Tag" value={vlan} onChange={(e) => setVlan(e.target.value.replace(/\D/g, ''))} hint="1 bis 4094" />
          <Notice tone="info">
            Ein Access-Port, dessen VLAN auf keinem Uplink getrunkt ist, isoliert das angeschlossene Gerät vom übrigen Netz.
          </Notice>
          <Button
            variant="primary"
            onClick={async () => {
              await run({
                type: 'switch/setPort',
                switchId: sw.id,
                portIndex: editing!,
                mode,
                vlanId: Number(vlan) || 1,
              });
              setEditing(null);
            }}
          >
            Übernehmen
          </Button>
        </div>
      </Modal>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Leitungen
// ---------------------------------------------------------------------------

const LINK_KIND_LABEL: Record<NetLink['kind'], string> = {
  'fiber-owned': 'Eigene Faser',
  'fiber-leased': 'Gemietete Faser',
  transit: 'IP-Transit',
  'peering-private': 'Direktpeering',
  'peering-ix': 'IXP-Port',
  internal: 'Intern',
};

/** Reserve-Umschalter für eine Transitstrecke. */
function StandbyToggle({ link }: { link: NetLink }) {
  const run = useGame((s) => s.run);
  if (link.kind !== 'transit') return null;

  return (
    <Button
      size="sm"
      variant="ghost"
      title={
        link.standby
          ? 'Wieder im Regelbetrieb mittragen lassen'
          : 'Als Reserve führen: trägt erst, wenn die Hauptstrecken ausfallen'
      }
      onClick={(e) => {
        e.stopPropagation();
        void run({ type: 'link/setStandby', linkId: link.id, standby: !link.standby });
      }}
    >
      {link.standby ? 'in Betrieb nehmen' : 'als Reserve'}
    </Button>
  );
}

export function Links() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const live = useGame((s) => s.live);
  const [buyTransit, setBuyTransit] = useState(false);
  const [buildFiber, setBuildFiber] = useState(false);

  if (!world) return null;
  // Live-Auslastung bevorzugen: Der geladene Weltzustand kann eine Minute alt sein.
  const liveById = new Map((live?.links ?? []).map((l) => [l.id, l]));
  const links = Object.values(world.links).map((link) => {
    const update = liveById.get(link.id);
    return update ? { ...link, state: update.state, utilizationMbps: update.utilizationMbps } : link;
  });

  const columns: Column<NetLink>[] = [
    {
      key: 'name',
      header: 'Strecke',
      sortValue: (l) => l.name,
      render: (l) => (
        <div>
          <p className="font-medium">{l.name}</p>
          <p className="text-2xs text-panel-500">
            {l.a.label} ↔ {l.b.label}
          </p>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Art',
      sortValue: (l) => l.kind,
      render: (l) => (
        <span className="inline-flex items-center gap-1.5">
          <Badge tone={l.kind === 'transit' ? 'warning' : l.kind.startsWith('peering') ? 'info' : 'neutral'}>
            {LINK_KIND_LABEL[l.kind]}
          </Badge>
          {l.standby && <Badge tone="muted">Reserve</Badge>}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      sortValue: (l) => l.state,
      render: (l) =>
        l.buildTicksRemaining > 0 ? (
          <StatusDot tone="info" label={`Bau, noch ${duration(l.buildTicksRemaining)}`} pulse />
        ) : (
          <StatusDot
            tone={l.state === 'up' ? 'success' : l.state === 'degraded' ? 'warning' : l.state === 'planned' ? 'info' : 'danger'}
            label={l.state === 'up' ? 'Aktiv' : l.state === 'degraded' ? 'Gestört' : l.state === 'planned' ? 'Geplant' : 'Ausgefallen'}
          />
        ),
    },
    {
      key: 'usage',
      header: 'Auslastung',
      sortValue: (l) => Math.max(l.utilizationMbps.aToB, l.utilizationMbps.bToA) / Math.max(1, l.capacityMbps),
      render: (l) => {
        const peak = Math.max(l.utilizationMbps.aToB, l.utilizationMbps.bToA);
        return (
          <div className="min-w-[8rem]">
            <Meter
              value={peak}
              max={l.capacityMbps}
              label={
                <>
                  <span className="tabular">{mbps(peak)}</span>
                  <span className="tabular">{mbps(l.capacityMbps)}</span>
                </>
              }
            />
          </div>
        );
      },
    },
    { key: 'latency', header: 'Latenz', align: 'right', hideOnMobile: true, sortValue: (l) => l.latencyMs, render: (l) => <span className="tabular text-xs">{dec(l.latencyMs, 2)} ms</span> },
    {
      key: 'cost',
      header: 'Kosten/Monat',
      align: 'right',
      sortValue: (l) => l.monthlyCost,
      render: (l) => (
        <div>
          <p className="tabular text-xs">{money(l.monthlyCost)}</p>
          {l.kind === 'transit' && (
            <p className="tabular text-2xs text-panel-500">95.: {mbps(billable95th(l))}</p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (l) => (
        <div className="flex justify-end gap-1">
          <StandbyToggle link={l} />
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm(`Strecke "${l.name}" kündigen?`)) void run({ type: 'link/cancel', linkId: l.id });
            }}
          >
            kündigen
          </Button>
        </div>
      ),
    },
  ];

  const transitCost = links.filter((l) => l.kind === 'transit').reduce((s, l) => s + l.monthlyCost, 0);

  return (
    <>
      <PageHeader
        title="Leitungen"
        description="Transit, Peering und der eigene Backbone zwischen den Standorten."
        actions={
          <>
            <Button onClick={() => setBuildFiber(true)}>Glasfaser bauen</Button>
            <Button variant="primary" onClick={() => setBuyTransit(true)}>
              Transit buchen
            </Button>
          </>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Strecken gesamt" value={num(links.length)} hint={`${links.filter((l) => l.state === 'up').length} aktiv`} />
        <Stat label="Transitkosten" value={money(transitCost)} hint="Grundgebühr pro Monat" />
        <Stat label="Gesamtkapazität" value={mbps(links.filter((l) => l.state === 'up').reduce((s, l) => s + l.capacityMbps, 0))} />
        <Stat
          label="Höchste Auslastung"
          value={pct100(
            Math.max(0, ...links.map((l) => (Math.max(l.utilizationMbps.aToB, l.utilizationMbps.bToA) / Math.max(1, l.capacityMbps)) * 100)),
            0,
          )}
          tone={links.some((l) => Math.max(l.utilizationMbps.aToB, l.utilizationMbps.bToA) / Math.max(1, l.capacityMbps) > 0.9) ? 'negative' : 'neutral'}
        />
      </StatGrid>

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={links} rowKey={(l) => l.id} empty="Keine Strecken vorhanden." />
      </Card>

      <BuyTransitDialog open={buyTransit} onClose={() => setBuyTransit(false)} />
      <BuildFiberDialog open={buildFiber} onClose={() => setBuildFiber(false)} />
    </>
  );
}

function BuyTransitDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [routerId, setRouterId] = useState('');
  const [providerAsn, setProviderAsn] = useState(0);
  const [capacity, setCapacity] = useState(1000);
  const [commit, setCommit] = useState(100);

  const routers = world ? Object.values(world.routers) : [];
  const router = world?.routers[routerId as never] ?? routers[0];
  const city = router ? world?.datacenters[router.datacenterId]?.city : undefined;
  const providers = (world?.market.providers ?? []).filter((p) => p.transitPricePerMbps > 0 && (!city || p.cities.includes(city)));
  const freePort = router?.ports.find((p) => p.linkId === null);
  const provider = providers.find((p) => p.asn === providerAsn) ?? providers[0];

  useEffect(() => {
    if (!routerId && routers.length) setRouterId(routers[0].id);
  }, [routers, routerId]);
  useEffect(() => {
    if (!providerAsn && providers.length) setProviderAsn(providers[0].asn);
  }, [providers, providerAsn]);

  const monthly = provider ? commit * provider.transitPricePerMbps + 150 : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="IP-Transit buchen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!router || !provider || !freePort}
            onClick={async () => {
              const result = await run({
                type: 'link/buyTransit',
                routerId: router!.id,
                portIndex: freePort!.index,
                providerAsn: provider!.asn,
                capacityMbps: capacity,
                committedMbps: commit,
              });
              if (result.ok) onClose();
            }}
          >
            Buchen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select label="Router" value={routerId} onChange={(e) => setRouterId(e.target.value)}>
          {routers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({world?.datacenters[r.datacenterId]?.city})
            </option>
          ))}
        </Select>

        {!freePort && <Notice tone="warning">Auf diesem Router ist kein Port frei.</Notice>}

        <Select label="Anbieter" value={providerAsn} onChange={(e) => setProviderAsn(Number(e.target.value))}>
          {providers.map((p) => (
            <option key={p.asn} value={p.asn}>
              {p.name} (AS{p.asn}) – {dec(p.transitPricePerMbps, 2)} €/Mbit/s, Reichweite {Math.round(p.reach * 100)} %
            </option>
          ))}
        </Select>
        {providers.length === 0 && <Notice tone="warning">Am Standort dieses Routers ist kein Transitanbieter präsent.</Notice>}

        <div className="grid grid-cols-2 gap-3">
          <Select label="Portgeschwindigkeit" value={capacity} onChange={(e) => setCapacity(Number(e.target.value))}>
            <option value={1000}>1 Gbit/s</option>
            <option value={10000}>10 Gbit/s</option>
            <option value={100000}>100 Gbit/s</option>
          </Select>
          <Input
            label="Mindestabnahme (Mbit/s)"
            type="number"
            value={commit}
            onChange={(e) => setCommit(Math.max(0, Number(e.target.value)))}
            hint="Wird immer berechnet."
          />
        </div>

        <Notice tone="info" title={`Etwa ${money(monthly)} pro Monat`}>
          Abgerechnet wird nach dem 95. Perzentil: Die höchsten 5 % der Messwerte eines Monats bleiben unberücksichtigt. Verkehr über der
          Mindestabnahme kostet zusätzlich {provider ? dec(provider.transitPricePerMbps, 2) : '–'} € je Mbit/s.
        </Notice>
      </div>
    </Modal>
  );
}

function BuildFiberDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [capacity, setCapacity] = useState(10000);
  const [owned, setOwned] = useState(false);

  const routers = world ? Object.values(world.routers) : [];
  const fromRouter = world?.routers[from as never];
  const toRouter = world?.routers[to as never];
  const fromPort = fromRouter?.ports.find((p) => p.linkId === null);
  const toPort = toRouter?.ports.find((p) => p.linkId === null);

  useEffect(() => {
    if (routers.length >= 2 && !from) {
      setFrom(routers[0].id);
      setTo(routers[1].id);
    }
  }, [routers, from]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Standorte verbinden"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!fromPort || !toPort || from === to}
            onClick={async () => {
              const result = await run({
                type: 'link/buildFiber',
                fromRouterId: fromRouter!.id,
                fromPort: fromPort!.index,
                toRouterId: toRouter!.id,
                toPort: toPort!.index,
                capacityMbps: capacity,
                owned,
              });
              if (result.ok) onClose();
            }}
          >
            Beauftragen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {routers.length < 2 ? (
          <Notice tone="warning">Für eine Backbone-Strecke werden zwei Router an unterschiedlichen Standorten benötigt.</Notice>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Von" value={from} onChange={(e) => setFrom(e.target.value)}>
                {routers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({world?.datacenters[r.datacenterId]?.city})
                  </option>
                ))}
              </Select>
              <Select label="Nach" value={to} onChange={(e) => setTo(e.target.value)}>
                {routers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({world?.datacenters[r.datacenterId]?.city})
                  </option>
                ))}
              </Select>
            </div>

            <Select label="Kapazität" value={capacity} onChange={(e) => setCapacity(Number(e.target.value))}>
              <option value={1000}>1 Gbit/s</option>
              <option value={10000}>10 Gbit/s</option>
              <option value={100000}>100 Gbit/s</option>
            </Select>

            <Select label="Beschaffung" value={owned ? 'owned' : 'leased'} onChange={(e) => setOwned(e.target.value === 'owned')}>
              <option value="leased">Mieten – geringe Einmalkosten, höhere Monatsrate, in einem Tag verfügbar</option>
              <option value="owned">Selbst bauen – hohe Baukosten und lange Bauzeit, danach sehr günstig</option>
            </Select>

            {(!fromPort || !toPort) && <Notice tone="warning">Auf mindestens einem der Router ist kein Port frei.</Notice>}

            <Notice tone="info">
              Beim Herstellen der Strecke wird automatisch eine iBGP-Sitzung zwischen beiden Routern eingerichtet, damit die Standorte
              gegenseitig ihre Routen kennen.
            </Notice>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// BGP
// ---------------------------------------------------------------------------

const SESSION_TONE: Record<BgpSession['state'], Tone> = {
  established: 'success',
  openconfirm: 'info',
  opensent: 'info',
  active: 'warning',
  connect: 'warning',
  idle: 'danger',
};

export function Bgp() {
  const world = useGame((s) => s.world);
  const live = useGame((s) => s.live);
  const gameId = useGame((s) => s.gameId);
  const run = useGame((s) => s.run);
  const [routerId, setRouterId] = useState<string>('');
  const [tab, setTab] = useState('table');
  const [routing, setRouting] = useState<Awaited<ReturnType<typeof api.routing>> | null>(null);
  const [neighborDialog, setNeighborDialog] = useState(false);

  const routers = useMemo(() => (world ? Object.values(world.routers) : []), [world]);
  useEffect(() => {
    if (!routerId && routers.length) setRouterId(routers[0].id);
  }, [routers, routerId]);

  // Routingtabelle separat laden – sie kann groß sein und gehört nicht in den Tick.
  useEffect(() => {
    if (!gameId || !routerId) return;
    void api.routing(gameId, routerId).then(setRouting).catch(() => setRouting(null));
  }, [gameId, routerId, live?.meta.tick]);

  if (!world) return null;
  const router = world.routers[routerId as never];
  const liveSessions = new Map((live?.sessions ?? []).map((s) => [s.id, s]));
  const sessions = (routing?.sessions ?? []).map((s) => liveSessions.get(s.id) ?? s);

  const entries = routing?.table.entries ?? [];

  const routeColumns: Column<RibEntry>[] = [
    { key: 'prefix', header: 'Präfix', sortValue: (e) => e.prefix, render: (e) => <Mono className="font-medium">{e.prefix}</Mono> },
    { key: 'nexthop', header: 'Next Hop', hideOnMobile: true, render: (e) => <Mono>{e.best.nextHop}</Mono> },
    {
      key: 'aspath',
      header: 'AS-Pfad',
      sortValue: (e) => e.best.asPath.length,
      render: (e) => <Mono>{e.best.asPath.length ? e.best.asPath.join(' ') : 'i'}</Mono>,
    },
    {
      key: 'relation',
      header: 'Quelle',
      sortValue: (e) => e.best.relation,
      render: (e) => (
        <Badge tone={e.best.relation === 'peer' ? 'info' : e.best.relation === 'transit' ? 'warning' : e.best.relation === 'local' ? 'success' : 'neutral'}>
          {e.best.relation === 'local' ? 'eigen' : relationLabel(e.best.relation as PeerRelation)}
        </Badge>
      ),
    },
    { key: 'lp', header: 'LocPrf', align: 'right', sortValue: (e) => e.best.localPref, render: (e) => <span className="tabular text-xs">{e.best.localPref}</span> },
    { key: 'med', header: 'MED', align: 'right', hideOnMobile: true, sortValue: (e) => e.best.med, render: (e) => <span className="tabular text-xs">{e.best.med}</span> },
    {
      key: 'alt',
      header: 'Alternativen',
      align: 'right',
      hideOnMobile: true,
      sortValue: (e) => e.alternatives.length,
      render: (e) => <span className="tabular text-xs">{e.alternatives.length}</span>,
    },
    { key: 'reason', header: 'Auswahlgrund', hideOnMobile: true, render: (e) => <span className="text-2xs text-panel-500 dark:text-panel-400">{e.reason}</span> },
  ];

  return (
    <>
      <PageHeader
        title="BGP"
        description="Sitzungen, Routingtabelle und Ankündigungen des eigenen autonomen Systems."
        actions={
          <>
            <Select value={routerId} onChange={(e) => setRouterId(e.target.value)} className="w-auto">
              {routers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
            <Button variant="primary" onClick={() => setNeighborDialog(true)}>
              Nachbarn anlegen
            </Button>
          </>
        }
      />

      <StatGrid cols={4}>
        <Stat label="Eigene ASN" value={`AS${world.company.asn}`} hint={router ? `Router-ID ${router.bgp.routerId}` : undefined} />
        <Stat
          label="Sitzungen aktiv"
          value={`${sessions.filter((s) => s.state === 'established').length} / ${sessions.length}`}
          tone={sessions.some((s) => s.state !== 'established') ? 'warning' : 'positive'}
        />
        <Stat label="Beste Pfade" value={num(entries.length)} hint={`aus ${num(routing?.table.totalPaths ?? 0)} Kandidaten`} />
        <Stat
          label="Angekündigte Präfixe"
          value={num(router?.bgp.originatedPrefixes.length ?? 0)}
          hint={router?.bgp.originatedPrefixes.join(', ')}
        />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'table', label: 'Routingtabelle' },
            { id: 'sessions', label: 'Sitzungen', badge: <Badge tone="muted">{sessions.length}</Badge> },
            { id: 'announce', label: 'Ankündigungen' },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'table' && (
        <Card className="mt-4" padded={false} subtitle="Beste Pfade je Ziel">
          <DataTable
            columns={routeColumns}
            rows={entries}
            rowKey={(e) => e.prefix}
            dense
            empty="Keine Routen – prüfe, ob eine Sitzung aufgebaut ist."
          />
        </Card>
      )}

      {tab === 'sessions' && (
        <div className="mt-4 space-y-3">
          {sessions.length === 0 && <EmptyState title="Keine Sitzungen" description="Lege einen Nachbarn an oder buche Transit." />}
          {sessions.map((session) => {
            const neighbor = routing?.neighbors.find((n) => n.id === session.peerId);
            return (
              <Card key={session.id}>
                <div className="flex flex-wrap items-center gap-4">
                  <StatusDot tone={SESSION_TONE[session.state]} pulse={session.state !== 'established' && session.state !== 'idle'} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      AS{session.remoteAsn} · {neighbor?.description ?? 'Nachbar'}
                    </p>
                    <p className="text-2xs text-panel-500">
                      {neighbor ? relationLabel(neighbor.relation) : '–'} · Zustand {session.state}
                      {session.lastError ? ` · ${session.lastError}` : ''}
                    </p>
                  </div>
                  <Field label="Empfangen">{num(session.prefixesReceived)}</Field>
                  <Field label="Gesendet">{num(session.prefixesSent)}</Field>
                  <Field label="Laufzeit">{duration(session.uptimeTicks)}</Field>
                  <Field label="Flaps">{session.flaps}</Field>
                  <div className="flex gap-1.5">
                    {neighbor && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          void run({
                            type: 'bgp/updateNeighbor',
                            routerId: routerId as never,
                            peerId: neighbor.id,
                            patch: { enabled: !neighbor.enabled },
                          })
                        }
                      >
                        {neighbor.enabled ? 'abschalten' : 'aktivieren'}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => void run({ type: 'bgp/clearSession', routerId: routerId as never, peerId: session.peerId })}>
                      zurücksetzen
                    </Button>
                  </div>
                </div>

                {neighbor && (
                  <div className="mt-3 grid grid-cols-2 gap-3 border-t divider pt-3 sm:grid-cols-5">
                    <Field label="Local Pref. eingehend">{neighbor.localPrefIn}</Field>
                    <Field label="Prepends ausgehend">{neighbor.prependOut}</Field>
                    <Field label="Max. Präfixe">{num(neighbor.maxPrefix)}</Field>
                    <Field label="Nur Default-Route">{neighbor.defaultOriginate ? 'ja' : 'nein'}</Field>
                    <Field label="Route-Maps">
                      {neighbor.routeMapIn ?? '–'} / {neighbor.routeMapOut ?? '–'}
                    </Field>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'announce' && router && <AnnouncementPanel routerId={router.id} />}

      <NeighborDialog open={neighborDialog} onClose={() => setNeighborDialog(false)} routerId={routerId} />
    </>
  );
}

function AnnouncementPanel({ routerId }: { routerId: string }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [blackhole, setBlackhole] = useState('');

  const router = world?.routers[routerId as never];
  if (!world || !router) return null;

  const prefixes = Object.values(world.prefixes).filter((p) => p.source !== 'rfc1918' && p.parentId === null);

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card title="Eigene Präfixe" subtitle="Was dieser Router in die Welt ankündigt">
        <div className="space-y-2">
          {prefixes.map((prefix) => {
            const cidr = cidrOf(prefix);
            const announced = router.bgp.originatedPrefixes.includes(cidr);
            return (
              <div key={prefix.id} className="surface-muted flex items-center gap-3 p-2.5">
                <StatusDot tone={announced ? 'success' : 'muted'} />
                <Mono className="min-w-0 flex-1 truncate font-medium">{cidr}</Mono>
                <Badge tone="muted">{prefix.family === 'ipv4' ? 'IPv4' : 'IPv6'}</Badge>
                <Button
                  size="sm"
                  variant={announced ? 'ghost' : 'primary'}
                  onClick={() =>
                    void run(
                      announced
                        ? { type: 'bgp/withdraw', routerId: router.id, prefix: cidr }
                        : { type: 'bgp/announce', routerId: router.id, prefix: cidr },
                    )
                  }
                >
                  {announced ? 'zurückziehen' : 'ankündigen'}
                </Button>
              </div>
            );
          })}
          {prefixes.length === 0 && <p className="text-xs text-panel-500">Keine eigenen Präfixe vorhanden.</p>}
        </div>
      </Card>

      <Card title="Blackhole" subtitle="Angriffsziel bei den Upstreams verwerfen lassen">
        <Notice tone="info">
          Ein Blackhole kündigt das Ziel mit der Community 65535:666 an. Die Upstreams verwerfen den Verkehr dann bereits in ihrem Netz – das
          angegriffene Ziel bleibt offline, der Rest des Netzes wird entlastet. Wirksam ist es nur mit aktivierter Blackhole-Community,
          einer aufgebauten Transit-Session und einem Prefix ab /24 (üblich: die einzelne Adresse als /32). Ein Blackhole auf die
          angegriffene Adresse beendet auch die Last eines laufenden Angriffs.
        </Notice>
        <div className="mt-3 flex gap-2">
          <Input
            label="Ziel-Präfix"
            className="flex-1"
            value={blackhole}
            onChange={(e) => setBlackhole(e.target.value)}
            placeholder="203.0.113.42/32"
          />
        </div>
        <div className="mt-2 flex gap-2">
          <Button variant="danger" size="sm" disabled={!blackhole} onClick={() => void run({ type: 'bgp/blackhole', routerId: router.id, prefix: blackhole, enable: true })}>
            Blackhole setzen
          </Button>
          <Button variant="ghost" size="sm" disabled={!blackhole} onClick={() => void run({ type: 'bgp/blackhole', routerId: router.id, prefix: blackhole, enable: false })}>
            aufheben
          </Button>
        </div>

        {router.bgp.routeMaps.length > 0 && (
          <div className="mt-4 border-t divider pt-3">
            <p className="label mb-1.5">Route-Maps</p>
            {router.bgp.routeMaps.map((map) => (
              <div key={map.name} className="mb-2">
                <p className="text-xs font-medium">{map.name}</p>
                <ul className="mt-0.5 space-y-0.5">
                  {map.clauses.map((clause) => (
                    <li key={clause.seq}>
                      <Mono className="text-panel-500">
                        {clause.seq} {clause.action}
                        {clause.match.prefixList ? ` match prefix-list ${clause.match.prefixList}` : ''}
                        {clause.set.localPref !== undefined ? ` set local-pref ${clause.set.localPref}` : ''}
                        {clause.set.addCommunity?.length ? ` set community ${clause.set.addCommunity.join(' ')}` : ''}
                      </Mono>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function NeighborDialog({ open, onClose, routerId }: { open: boolean; onClose: () => void; routerId: string }) {
  const run = useGame((s) => s.run);
  const [asn, setAsn] = useState('64500');
  const [address, setAddress] = useState('198.51.100.1');
  const [relation, setRelation] = useState<PeerRelation>('peer');
  const [description, setDescription] = useState('');
  const [localPref, setLocalPref] = useState('200');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="BGP-Nachbarn anlegen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              const result = await run({
                type: 'bgp/addNeighbor',
                routerId: routerId as never,
                neighbor: {
                  remoteAsn: Number(asn),
                  relation,
                  address,
                  description: description || `AS${asn}`,
                  localPrefIn: Number(localPref),
                },
              });
              if (result.ok) onClose();
            }}
          >
            Anlegen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Fremde ASN" value={asn} onChange={(e) => setAsn(e.target.value.replace(/\D/g, ''))} />
          <Input label="Nachbar-IP" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        <Select label="Beziehung" value={relation} onChange={(e) => setRelation(e.target.value as PeerRelation)}>
          <option value="peer">Peer – kostenloser Austausch eigener Netze</option>
          <option value="transit">Upstream – liefert die volle Tabelle, kostet Geld</option>
          <option value="customer">Kunde – bezahlt für Transit</option>
          <option value="ibgp">iBGP – interne Sitzung</option>
        </Select>
        <Input label="Beschreibung" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z. B. CloudEdge @ FRA-IX" />
        <Input
          label="Local Preference für eingehende Routen"
          value={localPref}
          onChange={(e) => setLocalPref(e.target.value.replace(/\D/g, ''))}
          hint="Höher gewinnt. Üblich: 200 für Peers, 100 für Upstreams – so bleibt teurer Transit die zweite Wahl."
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// IXP und Peering
// ---------------------------------------------------------------------------

export function Peering() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const metrics = useMetrics();
  const [joinIxp, setJoinIxp] = useState<Ixp | null>(null);

  const overview = useMemo(() => (world ? peeringOverview(world) : null), [world]);

  if (!world || !metrics || !overview) return null;
  const ixps = Object.values(world.ixps);
  const joined = ixps.filter((i) => i.joined);
  const relations = Object.values(world.peerings);
  const active = relations.filter((r) => r.state === 'active');
  const pending = relations.filter((r) => r.state === 'requested');

  return (
    <>
      <PageHeader
        title="Peering und Internet Exchanges"
        description="Direkter Verkehrsaustausch senkt Transitkosten und Latenz. Peering ist eine Beziehung – sie will beantragt und gepflegt werden."
      />

      <StatGrid cols={4}>
        <Stat label="Aktive Peerings" value={num(active.length)} hint={pending.length > 0 ? `${pending.length} Anfragen offen` : undefined} />
        <Stat label="Verkehr über Peering" value={mbps(metrics.derived.traffic.peeringMbps)} tone="positive" />
        <Stat
          label="Gesparte Transitkosten"
          value={`${money(overview.monthlySavings)}/Monat`}
          tone="positive"
          hint={overview.paidPeeringCost > 0 ? `abzüglich ${money(overview.paidPeeringCost)} bezahltes Peering` : undefined}
        />
        <Stat
          label="Anteil kostenpflichtig"
          value={pct100(metrics.derived.traffic.transitShare * 100, 0)}
          tone={metrics.derived.traffic.transitShare > 0.7 ? 'warning' : 'positive'}
          hint="je niedriger, desto günstiger"
        />
      </StatGrid>

      <HygienePanel />

      {active.length + pending.length > 0 && (
        <Card className="mt-4" title="Beziehungen" subtitle="Laufende Peerings und offene Anfragen.">
          <div className="space-y-1.5">
            {[...pending, ...active].map((relation) => {
              const provider = world.market.providers.find((p) => p.asn === relation.peerAsn);
              return (
                <div key={relation.id} className="flex flex-wrap items-center gap-2 rounded border divider p-2 text-xs">
                  <StatusDot tone={relation.state === 'active' ? 'success' : 'info'} />
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {provider?.name ?? `AS${relation.peerAsn}`} <Mono className="text-panel-500">AS{relation.peerAsn}</Mono>
                  </span>
                  <Badge tone="neutral">{describeVia(world, relation.via)}</Badge>
                  {relation.state === 'active' ? (
                    <>
                      <span className="text-panel-500 dark:text-panel-400">
                        {relation.ratio > 0 ? `${relation.ratio.toFixed(1)} : 1` : '–'} · {mbps(relation.trafficMbps)}
                      </span>
                      {relation.monthlyCost > 0 && <Badge tone="warning">{money(relation.monthlyCost)}/Monat</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => void run({ type: 'peering/terminate', relationId: relation.id })}>
                        kündigen
                      </Button>
                    </>
                  ) : (
                    <span className="text-panel-500 dark:text-panel-400">
                      Antwort in {Math.max(0, Math.round(((relation.decidesAtTick ?? 0) - world.meta.tick) / 24))} Tagen
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {joined.length > 0 && overview.candidates.length > 0 && (
        <Card
          className="mt-4"
          title="Mögliche Partner"
          subtitle="Nach Reichweite sortiert – oben liegt das größte Sparpotenzial. Bei einer Absage steht daneben, woran es fehlt."
        >
          <div className="space-y-2">
            {overview.candidates.slice(0, 12).map((candidate) => (
              <CandidateRow key={candidate.provider.asn} candidate={candidate} />
            ))}
          </div>
        </Card>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {ixps.map((ixp) => (
          <Card
            key={ixp.id}
            title={
              <span className="flex items-center gap-2">
                {ixp.name}
                {ixp.joined && <Badge tone="success">Mitglied</Badge>}
                {ixp.routeServer.connected && <Badge tone="info">Route-Server</Badge>}
              </span>
            }
            subtitle={`${ixp.city} · ${num(ixp.memberCount)} Teilnehmer · deckt etwa ${Math.round(ixp.trafficCoverage * 100)} % des Verkehrs ab`}
            actions={
              ixp.joined ? (
                <Button size="sm" variant="ghost" onClick={() => void run({ type: 'ixp/leave', ixpId: ixp.id })}>
                  austreten
                </Button>
              ) : (
                <Button size="sm" variant="primary" onClick={() => setJoinIxp(ixp)}>
                  beitreten
                </Button>
              )
            }
          >
            <div className="mb-3 grid grid-cols-3 gap-2">
              <Field label="Portgebühr">{money(ixp.portFeePerGbps)}/Gbit/s</Field>
              <Field label="Einrichtung">{money(ixp.setupFee)}</Field>
              <Field label="Gebuchter Port">{ixp.portCapacityMbps ? mbps(ixp.portCapacityMbps) : '–'}</Field>
            </div>

            {ixp.joined && <IxpPortPanel ixp={ixp} />}

            {ixp.joined && ixp.routeServer.available && !ixp.routeServer.connected && (
              <Notice tone="info" title="Route-Server verfügbar">
                Eine einzige Sitzung erschließt alle offenen Mitglieder – ohne einzelne Verhandlung.
                {ixp.routeServer.requiresRpki && ' Der Knoten verlangt dafür signierte Routen.'}
                <div className="mt-2">
                  <Button size="sm" variant="primary" onClick={() => void run({ type: 'ixp/connectRouteServer', ixpId: ixp.id })}>
                    Mit Route-Server verbinden
                  </Button>
                </div>
              </Notice>
            )}
            {ixp.joined && !ixp.routeServer.available && (
              <p className="text-2xs text-panel-500 dark:text-panel-400">
                Dieser Knoten betreibt keinen Route-Server – hier muss mit jedem Netz einzeln verhandelt werden.
              </p>
            )}
          </Card>
        ))}
      </div>

      <JoinIxpDialog ixp={joinIxp} onClose={() => setJoinIxp(null)} />
    </>
  );
}

/** Eine Zeile der Kandidatenliste samt Begründung, wenn es (noch) nicht reicht. */
function CandidateRow({ candidate }: { candidate: ReturnType<typeof peeringOverview>['candidates'][number] }) {
  const run = useGame((s) => s.run);
  const [open, setOpen] = useState(false);
  const { provider, via, assessment } = candidate;
  const missing = assessment.requirements.filter((r) => !r.met);

  return (
    <div className="rounded border divider p-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <StatusDot tone={assessment.accepted ? 'success' : missing.length > 1 ? 'danger' : 'warning'} />
        <span className="min-w-0 flex-1 truncate font-medium">
          {provider.name} <Mono className="text-panel-500">AS{provider.asn}</Mono>
        </span>
        <Badge tone={provider.kind === 'tier1' ? 'warning' : provider.kind === 'content' ? 'info' : 'neutral'}>{provider.kind}</Badge>
        <span className="text-panel-500 dark:text-panel-400">{Math.round(provider.reach * 100)} % Reichweite</span>
        {missing.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
            {open ? 'weniger' : `${missing.length} offen`}
          </Button>
        )}
        <Button
          size="sm"
          variant={assessment.accepted ? 'primary' : 'secondary'}
          onClick={() => void run({ type: 'peering/request', peerAsn: provider.asn, via })}
        >
          anfragen
        </Button>
      </div>

      {open && (
        <ul className="mt-2 space-y-1 border-t divider pt-2">
          {assessment.requirements.map((requirement) => (
            <li key={requirement.label} className="flex gap-2 text-2xs">
              <span className={requirement.met ? 'text-accent-600 dark:text-accent-400' : 'text-amber-600 dark:text-amber-400'}>
                {requirement.met ? '✓' : '×'}
              </span>
              <span>
                <span className="font-medium">{requirement.label}</span> — {requirement.detail}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Auslastung des IXP-Ports.
 *
 * Peering-Verkehr taucht auf keiner Rechnung auf – deshalb fällt ein volllaufender
 * Port erst auf, wenn Kunden sich beschweren. Hier steht er sichtbar.
 */
function IxpPortPanel({ ixp }: { ixp: Ixp }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  if (!world) return null;

  const load = ixpPortLoad(world, ixp.id);
  const next = ixp.portCapacityMbps >= 100_000 ? null : ixp.portCapacityMbps >= 10_000 ? 100_000 : 10_000;

  return (
    <div className="mb-3">
      <Meter value={load.usedMbps} max={Math.max(1, load.capacityMbps)} label={`Portauslastung ${mbps(load.usedMbps)} von ${mbps(load.capacityMbps)}`} />
      {load.share > 0.85 && (
        <Notice tone="warning" title="Port läuft voll">
          Über diesen Port geht kostenloser Verkehr verloren, sobald er dicht ist.
          {next && (
            <div className="mt-2">
              <Button size="sm" variant="primary" onClick={() => void run({ type: 'ixp/upgradePort', ixpId: ixp.id, capacityMbps: next })}>
                Auf {mbps(next)} ausbauen
              </Button>
            </div>
          )}
        </Notice>
      )}
    </div>
  );
}

/** RPKI, IRR und MANRS – Eintrittspreis für ernsthaftes Peering. */
function HygienePanel() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  if (!world) return null;
  const hygiene = world.company.hygiene;

  const measures: HygieneMeasure[] = ['rpkiSigned', 'rpkiValidating', 'irrRegistered', 'irrFiltering'];
  const done = measures.filter((m) => hygiene[m]).length;

  return (
    <Card
      className="mt-4"
      title="Routing-Hygiene"
      subtitle="Ohne signierte Routen und gepflegte IRR-Objekte lehnen seriöse Netze das Peering ab."
      actions={
        <Badge tone={hygiene.manrsCompliant ? 'success' : done > 0 ? 'warning' : 'muted'}>
          {hygiene.manrsCompliant ? 'MANRS-konform' : `${done} von 4`}
        </Badge>
      }
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {measures.map((measure) => (
          <div key={measure} className="flex items-start gap-2 rounded border divider p-2">
            <StatusDot tone={hygiene[measure] ? 'success' : 'muted'} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium">{HYGIENE_LABEL[measure]}</p>
              <p className="mt-0.5 text-2xs text-panel-500 dark:text-panel-400">{HYGIENE_DESCRIPTION[measure]}</p>
              {!hygiene[measure] && (
                <Button
                  size="sm"
                  className="mt-1.5"
                  onClick={() => void run({ type: 'net/enableHygiene', measure })}
                >
                  Einrichten — {money(HYGIENE_COSTS[measure].setup)} + {money(HYGIENE_COSTS[measure].monthly)}/Monat
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function JoinIxpDialog({ ixp, onClose }: { ixp: Ixp | null; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [routerId, setRouterId] = useState('');
  const [capacity, setCapacity] = useState(1000);

  const candidates = world ? Object.values(world.routers).filter((r) => world.datacenters[r.datacenterId]?.city === ixp?.city) : [];
  useEffect(() => {
    if (candidates.length && !routerId) setRouterId(candidates[0].id);
  }, [candidates, routerId]);

  if (!ixp) return null;
  const router = world?.routers[routerId as never] ?? candidates[0];
  const port = router?.ports.find((p) => p.linkId === null);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${ixp.name} beitreten`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={!router || !port}
            onClick={async () => {
              const result = await run({
                type: 'ixp/join',
                ixpId: ixp.id,
                routerId: router!.id,
                portIndex: port!.index,
                portCapacityMbps: capacity,
              });
              if (result.ok) onClose();
            }}
          >
            Beitreten ({money(ixp.setupFee)})
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {candidates.length === 0 ? (
          <Notice tone="warning">
            Für einen Anschluss an {ixp.name} wird ein Router in {ixp.city} benötigt. Eröffne dort zuerst einen Standort.
          </Notice>
        ) : (
          <>
            <Select label="Router" value={routerId} onChange={(e) => setRouterId(e.target.value)}>
              {candidates.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
            <Select label="Portkapazität" value={capacity} onChange={(e) => setCapacity(Number(e.target.value))}>
              <option value={1000}>1 Gbit/s – {money(ixp.portFeePerGbps)} pro Monat</option>
              <option value={10000}>10 Gbit/s – {money(ixp.portFeePerGbps * 10)} pro Monat</option>
              <option value={100000}>100 Gbit/s – {money(ixp.portFeePerGbps * 100)} pro Monat</option>
            </Select>
            {!port && <Notice tone="warning">Auf dem gewählten Router ist kein Port frei.</Notice>}
            <Notice tone="info">
              Nach dem Beitritt müssen die einzelnen Peerings noch angefragt werden. Content-Netze stimmen fast immer zu, Tier-1-Netze
              grundsätzlich nicht.
            </Notice>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// IPAM
// ---------------------------------------------------------------------------

/**
 * Aktionen je Adressblock.
 *
 * Der Verkauf steht nur dort, wo er auch möglich ist – und nennt sonst den
 * Grund. Ein Knopf, der bei jedem zweiten Block eine Fehlermeldung wirft, wäre
 * schlechter als keiner.
 */
function PrefixActions({ prefix, onSplit }: { prefix: IpPrefix; onSplit: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);

  const check = useMemo(() => (world ? canSellPrefix(world, prefix.id) : null), [world, prefix.id]);
  const children = useMemo(
    () => (world ? Object.values(world.prefixes).filter((p) => p.parentId === prefix.id) : []),
    [world, prefix.id],
  );
  const assigned = useMemo(() => (world ? assignedAddressCount(world, prefix) : 0), [world, prefix]);

  if (!world || !check) return null;

  const tradable = prefix.source !== 'rfc1918' && prefix.source !== 'upstream' && !prefix.parentId;

  return (
    <div className="flex flex-wrap justify-end gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          onSplit();
        }}
      >
        unterteilen
      </Button>

      {/* Unterteilung rückgängig machen – sonst wäre der Block für immer zerlegt. */}
      {children.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          title={`${children.length} Subnetze wieder einsammeln`}
          onClick={(e) => {
            e.stopPropagation();
            void run({ type: 'ipam/mergeSubnets', prefixId: prefix.id });
          }}
        >
          zusammenführen
        </Button>
      )}

      {/* Der übliche Weg zum Abgeben: erst zurückziehen, dann verkaufen. */}
      {prefix.announced && tradable && (
        <Button
          size="sm"
          variant="ghost"
          title={
            assigned > 0
              ? `Achtung: ${assigned} vergebene Adressen werden dadurch unerreichbar`
              : 'Ankündigung zurückziehen – Voraussetzung für die Abgabe'
          }
          onClick={(e) => {
            e.stopPropagation();
            if (
              assigned === 0 ||
              confirm(
                `${cidrOf(prefix)} nicht mehr ankündigen?\n\n` +
                  `${assigned} vergebene Adressen sind danach aus dem Internet nicht mehr erreichbar.`,
              )
            ) {
              void run({ type: 'ipam/withdrawBlock', prefixId: prefix.id });
            }
          }}
        >
          zurückziehen
        </Button>
      )}

      {check.ok ? (
        <Button
          size="sm"
          variant="danger"
          title={`Bringt ${money(check.proceeds)} und spart ${money(check.monthlySaving)} im Monat`}
          onClick={(e) => {
            e.stopPropagation();
            if (
              confirm(
                `${cidrOf(prefix)} abgeben?\n\n` +
                  (check.proceeds > 0 ? `Erlös: ${money(check.proceeds)}\n` : 'Erlös: keiner (IPv6 ist frei verfügbar)\n') +
                  `Gespart: ${money(check.monthlySaving)} pro Monat\n\n` +
                  'Der Block ist danach weg.',
              )
            ) {
              void run({ type: 'ipam/sellBlock', prefixId: prefix.id });
            }
          }}
        >
          {check.proceeds > 0 ? 'verkaufen' : 'zurückgeben'}
        </Button>
      ) : (
        <span
          className="cursor-help self-center px-1 text-2xs text-panel-400 dark:text-panel-500"
          title={check.reason ?? 'Abgabe nicht möglich'}
        >
          {prefix.source === 'rfc1918' ? 'privat' : prefix.parentId ? 'Subnetz' : 'gebunden'}
        </span>
      )}
    </div>
  );
}

export function Ipam() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const metrics = useMetrics();
  const [family, setFamily] = useState<'ipv4' | 'ipv6'>('ipv4');
  const [splitting, setSplitting] = useState<string | null>(null);
  const [newLength, setNewLength] = useState('26');

  if (!world || !metrics) return null;

  const prefixes = Object.values(world.prefixes).filter((p) => p.family === family);
  const usedV4 = metrics.derived.capacity.ipv4Used;
  const totalV4 = metrics.derived.capacity.ipv4Total;

  const columns: Column<(typeof prefixes)[number]>[] = [
    {
      key: 'cidr',
      header: 'Präfix',
      sortValue: (p) => p.network,
      render: (p) => (
        <div className={p.parentId ? 'pl-4' : ''}>
          <Mono className="font-medium">{cidrOf(p)}</Mono>
          {p.parentId && <span className="ml-1 text-2xs text-panel-400">Subnetz</span>}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Herkunft',
      sortValue: (p) => p.source,
      render: (p) => (
        <Badge tone={p.source === 'rir' ? 'success' : p.source === 'purchased' ? 'info' : p.source === 'rfc1918' ? 'muted' : 'neutral'}>
          {p.source === 'rir' ? 'RIR' : p.source === 'purchased' ? 'gekauft' : p.source === 'upstream' ? 'Upstream' : p.source === 'rfc1918' ? 'privat' : 'intern'}
        </Badge>
      ),
    },
    { key: 'usage', header: 'Verwendung', sortValue: (p) => p.usage, render: (p) => <span className="text-xs">{p.usage}</span> },
    {
      key: 'announced',
      header: 'Angekündigt',
      align: 'center',
      render: (p) => (p.announced ? <StatusDot tone="success" label="ja" /> : <StatusDot tone="muted" label="nein" />),
    },
    { key: 'rdns', header: 'Reverse DNS', align: 'center', hideOnMobile: true, render: (p) => (p.rdnsDelegated ? <Badge tone="success">delegiert</Badge> : <Badge tone="muted">fehlt</Badge>) },
    { key: 'cost', header: 'Kosten/Monat', align: 'right', hideOnMobile: true, sortValue: (p) => p.monthlyCost, render: (p) => <span className="tabular text-xs">{money(p.monthlyCost)}</span> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (p) => <PrefixActions prefix={p} onSplit={() => setSplitting(p.id)} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="IP-Adressverwaltung"
        description="Präfixe, Subnetze und Ankündigungen."
        actions={
          <>
            <Button onClick={() => void run({ type: 'ipam/buyBlock', family: 'ipv4', length: 24 })}>IPv4-/24 kaufen</Button>
            <Button variant="primary" onClick={() => void run({ type: 'ipam/buyBlock', family: 'ipv6', length: 32 })}>
              IPv6-Block beziehen
            </Button>
          </>
        }
      />

      <StatGrid cols={4}>
        <Stat
          label="IPv4 belegt"
          value={`${num(usedV4)} / ${num(totalV4)}`}
          tone={totalV4 > 0 && usedV4 / totalV4 > 0.9 ? 'negative' : usedV4 / Math.max(1, totalV4) > 0.75 ? 'warning' : 'neutral'}
          hint={totalV4 > 0 ? pct100((usedV4 / totalV4) * 100, 0) + ' vergeben' : undefined}
        />
        <Stat label="IPv4-Präfixe" value={num(Object.values(world.prefixes).filter((p) => p.family === 'ipv4').length)} />
        <Stat label="IPv6-Präfixe" value={num(Object.values(world.prefixes).filter((p) => p.family === 'ipv6').length)} />
        <Stat
          label="Kosten IP-Adressen"
          value={money(Object.values(world.prefixes).reduce((s, p) => s + p.monthlyCost, 0))}
          hint="pro Monat"
        />
      </StatGrid>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'ipv4', label: 'IPv4' },
            { id: 'ipv6', label: 'IPv6' },
          ]}
          active={family}
          onChange={(id) => setFamily(id as 'ipv4' | 'ipv6')}
        />
      </div>

      <Card className="mt-4" padded={false}>
        <DataTable columns={columns} rows={prefixes} rowKey={(p) => p.id} empty="Keine Präfixe in dieser Familie." />
      </Card>

      <Modal
        open={splitting !== null}
        onClose={() => setSplitting(null)}
        title="Präfix unterteilen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSplitting(null)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                const result = await run({ type: 'ipam/split', prefixId: splitting as never, newLength: Number(newLength) });
                if (result.ok) setSplitting(null);
              }}
            >
              Unterteilen
            </Button>
          </>
        }
      >
        <Input
          label="Neue Präfixlänge"
          value={newLength}
          onChange={(e) => setNewLength(e.target.value.replace(/\D/g, ''))}
          hint="Aus einem /24 werden mit /26 vier Subnetze, mit /28 sechzehn."
        />
      </Modal>
    </>
  );
}

export { LINK_KIND_LABEL };
